'use es6';

import EmailAssetDialog from './EmailAssetDialog';
import { ReportActionsFloatingAlertStore } from 'reporting-action-components/shared/ReportActionsFloatingAlertStore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMutation } from 'data-fetching-client';
import { SEND_ASSET_NOW, CREATE_ASSET_SCHEDULE, EDIT_ASSET_SCHEDULE } from 'dashboard-lib/public/emails/emails';
import { useHideDialog } from '../../../dialog/ducks';
import PropTypes from 'prop-types';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { getDialogText, sendNowSuccessMessage } from '../../shareAsset/shareAssetAlertMessages';
import { SHARE_EMAIL } from '../../shareAsset/shareTypes';
import { showSuccessNotification } from '../../shared/reportActionAlerts';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
const scheduledSharedAssetPropDefault = {};
const assetSpecificPayloadPropDefault = {};
const assetSpecificTrackingPropsPropDefault = {};
const EmailAssetDialogContainer = ({
  appUsageTracker,
  onEditSuccess = noop,
  scheduledSharedAsset = scheduledSharedAssetPropDefault,
  assetSpecificPayload = assetSpecificPayloadPropDefault,
  validationError = false,
  objectId,
  objectType,
  assetSpecificTrackingProps = assetSpecificTrackingPropsPropDefault,
  assetName = '',
  previewUrl,
  renderAdditionalFields = noop,
  resetSelectedReports = noop
}) => {
  const usageTracker = useTracker(appUsageTracker);
  const hideDialog = useHideDialog();
  const [sendEmailNowMutation] = useMutation(SEND_ASSET_NOW);
  const [createEmailScheduleMutation] = useMutation(CREATE_ASSET_SCHEDULE);
  const [editEmailScheduleMutation] = useMutation(EDIT_ASSET_SCHEDULE);
  const getDomainsFromExternalRecipientEmails = externalRecipientEmails => {
    return [...new Set(externalRecipientEmails.map(email => email.split('@')[1]))];
  };
  const sendEmailNow = ({
    payload
  }) => {
    if (usageTracker) {
      usageTracker.track(EVENTS.sendReportShareAssetNow, Object.assign({
        object: 'email',
        actionType: 'share',
        action: 'send-email',
        interactionSource: 'dialog',
        interactionWith: 'asset-action',
        assetType: objectType.toLowerCase(),
        externalEmailDomains: getDomainsFromExternalRecipientEmails(payload.externalRecipientEmails),
        exportType: payload.exportType
      }, assetSpecificTrackingProps));
    }
    sendEmailNowMutation({
      variables: Object.assign({
        objectId,
        objectType
      }, payload, assetSpecificPayload)
    }).then(() => {
      sendNowSuccessMessage(SHARE_EMAIL);
      hideDialog();
    }).catch(err => {
      const errObj = Object.getOwnPropertyDescriptor(err, 'networkError').value;
      if (errObj.status === 403) {
        const {
          responseJSON: {
            externalEmailsLimitDescription
          }
        } = errObj;
        ReportActionsFloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.emailNowWExceedsExternalEmailsLimit.title",
            options: {
              totalLimit: externalEmailsLimitDescription.totalLimit
            }
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.emailNowWExceedsExternalEmailsLimit.description",
            options: {
              currentUsage: externalEmailsLimitDescription.currentUsage
            }
          }),
          type: 'danger'
        });
      } else {
        ReportActionsFloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.shareAssetNowError.title",
            options: {
              dialog: getDialogText(SHARE_EMAIL)
            }
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.shareAssetNowError.description"
          }),
          type: 'danger'
        });
      }
    });
  };
  const createOrEditEmailSchedule = ({
    payload
  }) => {
    const {
      scheduledEmailId
    } = payload;
    if (usageTracker) {
      if (scheduledEmailId) {
        usageTracker.track(EVENTS.updateSavedRecurringShareAsset, Object.assign({
          object: 'email',
          actionType: 'save',
          action: 'edit-schedule',
          interactionSource: 'dialog',
          interactionWith: 'asset-action',
          assetType: objectType.toLowerCase(),
          exportType: payload.exportType
        }, assetSpecificTrackingProps));
      } else {
        usageTracker.track(EVENTS.createRecurringShareAssets, Object.assign({
          object: 'email',
          actionType: 'share',
          action: 'create-recurring-email',
          interactionSource: 'dialog',
          interactionWith: 'asset-action',
          assetType: objectType.toLowerCase(),
          exportType: payload.exportType
        }, assetSpecificTrackingProps));
      }
    }
    const mutation = scheduledEmailId ? editEmailScheduleMutation : createEmailScheduleMutation;
    mutation({
      variables: Object.assign({
        objectId,
        objectType
      }, payload, assetSpecificPayload)
    }).then(() => {
      showSuccessNotification({
        message: 'reporting-action-components.actions.emailReport.dialog.alert.scheduleShareAssetSuccess',
        options: {
          dialog: getDialogText(SHARE_EMAIL)
        }
      });
      if (scheduledEmailId) {
        onEditSuccess();
      }
      hideDialog();
    }).catch(() => {
      ReportActionsFloatingAlertStore.addAlert({
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.alert.scheduleShareAssetError.title",
          options: {
            dialog: getDialogText(SHARE_EMAIL)
          }
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.alert.scheduleShareAssetError.description"
        }),
        type: 'danger'
      });
    });
  };
  const sendOrScheduleEmail = ({
    isSchedulingEmail,
    payload
  }) => {
    if (!isSchedulingEmail) {
      sendEmailNow({
        payload
      });
    } else {
      createOrEditEmailSchedule({
        payload
      });
    }
  };
  return /*#__PURE__*/_jsx(EmailAssetDialog, Object.assign({
    appUsageTracker: appUsageTracker,
    assetSpecificPayload: assetSpecificPayload,
    onSubmit: sendOrScheduleEmail,
    onClose: hideDialog,
    isEditingScheduledEmail: Object.keys(scheduledSharedAsset).length > 0,
    validationError: validationError,
    assetName: assetName,
    previewUrl: previewUrl,
    objectType: objectType,
    renderAdditionalFields: renderAdditionalFields,
    resetSelectedReports: resetSelectedReports
  }, scheduledSharedAsset));
};
EmailAssetDialogContainer.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  onEditSuccess: PropTypes.func,
  scheduledSharedAsset: PropTypes.object,
  assetSpecificPayload: PropTypes.object,
  validationError: PropTypes.bool,
  objectId: PropTypes.number.isRequired,
  objectType: PropTypes.string.isRequired,
  assetSpecificTrackingProps: PropTypes.object,
  assetName: PropTypes.string,
  previewUrl: PropTypes.string.isRequired,
  renderAdditionalFields: PropTypes.func,
  resetSelectedReports: PropTypes.func
};
export default EmailAssetDialogContainer;